import { Injectable } from '@angular/core';
import { BranchLocationService } from '../services/loan/branch-location.api.service';
import { BranchLocationModel } from '../../shared/models/branch-location.model';
import { lastValueFrom } from 'rxjs';
import { RealEstateTypeModel } from '../../shared/models/real-estate-type.model';
import { RealEstateTypesService } from '../services/loan/real-estate-types.api.service';
import { StepTwoState } from './loan-steps/step-two-state';

/** Route segments are step-one … step-seven; when the co-applicant step is skipped, the UI uses 6 numbered steps instead of 7. */
@Injectable({
    providedIn: 'root',
})
export class BusinessLoanState {
    public readonly MAX_PARTS: number = 7 as const;
    private _currentPart: number = 1;
    public branchLocations: BranchLocationModel[] = [];
    public realEstateTypes: RealEstateTypeModel[] = [];

    constructor(
        private branchLocationsService: BranchLocationService,
        private realEstateService: RealEstateTypesService,
        private stepTwoState: StepTwoState,
    ) {
        this.getBranchLocations().then(l => this.branchLocations = l);
        this.getRealEstateTypes().then(r => this.realEstateTypes = r);
    }

    get currentPart(): number {
        return this._currentPart;
    }

    set currentPart(value: number) {
        this._currentPart = Math.min(Math.max(value, 1), this.maxLoanSteps);
    }

    /** True when the applicant chose to apply individually (route step 3 is not used). */
    get skipsCoapplicantRouteStep(): boolean {
        return this.stepTwoState.state?.isIndividualApplicant === true;
    }

    /** Number of progress segments / visible step labels (6 or 7). */
    get maxLoanSteps(): number {
        return this.skipsCoapplicantRouteStep ? 6 : 7;
    }

    /**
     * Maps a route step index (1 = step-one … 7 = step-seven) to the sequential step number shown in the UI.
     */
    displayStepForRouteStep(routeStep: number): number {
        if (routeStep < 1) return 1;
        if (!this.skipsCoapplicantRouteStep || routeStep <= 3) return routeStep;
        return routeStep - 1;
    }

    setCurrentPartFromRouteStep(routeStep: number): void {
        this._currentPart = this.displayStepForRouteStep(routeStep);
    }

    private async getBranchLocations() {
        if (this.branchLocations.length !== 0) return this.branchLocations;
        this.branchLocations = await lastValueFrom(this.branchLocationsService.getLoanBranchLocations());
        return this.branchLocations;
    }

    private async getRealEstateTypes() {
        if (this.realEstateTypes.length !== 0) return this.realEstateTypes;
        this.realEstateTypes = await lastValueFrom(this.realEstateService.getAll());
        return this.realEstateTypes;
    }

    get realEstateOptions() {
        return this.realEstateTypes.map(l => this.nameFromRealEstateUUID(l.uuid) ?? "");
    }
    get realEstateValues() {
        return this.realEstateTypes.map(l => l.uuid);
    }

    get locationOptions() {
        return this.branchLocations.map(l => this.nameFromLocationUUID(l.uuid) ?? "");
    }
    get locationValues() {
        return this.branchLocations.map(l => l.uuid);
    }

    displayBranchLocationName(model: BranchLocationModel) {
        const name = model.name?.trim();
        if (name) return name.split(' - ')[0];
        return `${model.address}, ${model.city}, ${model.state}`;
    }

    nameFromLocationUUID(uuid?: string): string | undefined {
        if (!uuid) return undefined;
        const loc = this.branchLocations.find(l => l.uuid === uuid);
        if (!loc) return undefined;
        return this.displayBranchLocationName(loc)
    }

    nameFromRealEstateUUID(uuid?: string): string | undefined {
        if (!uuid) return undefined;
        const loc = this.realEstateTypes.find(r => r.uuid === uuid);
        if (!loc) return undefined;
        return loc.name;
    }
}
